import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { loadingReducer, loadingReducerInitialState } from '../../../reducer/loadingReducer';
import {
  LessonEditStatus,
  LessonFlag,
  LessonItemFragment,
  useAssignTeachersToLessonsMutation,
  useAttachLessonsToTeachingBlockVersionMutation,
  useCopyLessonIntoTeachingBlockVersionMutation,
  useCreateLessonInTeachingBlockVersionMutation,
  useCreateTeachingBlockCardsMutation,
  useDeleteLessonsMutation,
  useDeleteTeachingBlockCardsMutation,
  useDetachLessonsFromTeachingBlockVersionMutation,
  useDetachTeachersFromLessonsMutation,
  useLessonsForTeachingBlockVersionQuery,
  useSetLessonsFlagMutation,
  useUpdateLessonMutation,
} from '../../../types/planung-graphql-client-defs';
import { isUuid } from '../../../utils/typeguards';
import {
  refreshTeachingBlockVersionQueries,
  useTeachingBlockVersionData,
} from '../../TeachingBlockVersion/hooks/useTeachingBlockVersionData';
import { ClassInterfaceType, LessonTableType } from '../TimetableVersion/Table/TimetableVersionLessonsTable';
import { VersionLessonFormType } from '../types';
import { toCardDurations, toLessonInput } from '../utils/buildLessonInput';

type LessonCheckbox = 'teachingLoadEnabled' | 'timetableEnabled' | 'elective';

const checkboxFlags: Record<LessonCheckbox, LessonFlag> = {
  teachingLoadEnabled: LessonFlag.TeachingLoadEnabled,
  timetableEnabled: LessonFlag.TimetableEnabled,
  elective: LessonFlag.Elective,
};

const NO_LESSONS: LessonItemFragment[] = [];

const invalid = (uuids: string[]) => !uuids.length || !uuids.every((uuid) => isUuid(uuid));

const byGrade = <T extends { class: { grade?: number | null } }>(a: T, b: T) =>
  (a.class.grade ?? 0) - (b.class.grade ?? 0);

/** Formularwerte einer Zuordnung der Epochenplan-Version; Klassen nach Jahrgang. */
/** Zeile der Epochenplan-Zuordnungen: wie im Stundenplan, aber mit dem Kennzeichen der Epochenplan-Version. */
export type TeachingBlockLessonTableType = Omit<LessonTableType, 'onlyInTimetableVersion'> & {
  onlyInTeachingBlockVersion: boolean;
};

export const teachingBlockLessonToFormType = (lesson: LessonItemFragment): VersionLessonFormType => ({
  uuid: lesson.uuid,
  subject: { uuid: lesson.subject.uuid, name: lesson.subject.name },
  lessonClasses: [...lesson.lessonClasses].sort(byGrade).map((lessonClass) => ({
    uuid: lessonClass.uuid,
    groups: lessonClass.groups.map((group) => ({ name: group.name, uuid: group.uuid })),
    class: {
      uuid: lessonClass.class.uuid,
      name: lessonClass.class.name,
      color: lessonClass.class.color,
      grade: lessonClass.class.grade,
    },
  })),
  comment: lesson.comment,
  elective: lesson.elective,
  teachingLoadEnabled: lesson.teachingLoadEnabled,
  timetableEnabled: lesson.timetableEnabled,
  subTitle: lesson.subTitle,
  isClassTrip: lesson.isClassTrip,
  roomSupply: lesson.roomSupply.map((room) => ({ uuid: room.uuid, description: room.descriptions })),
  teachers: lesson.teachers.map((teacher) => ({
    present: teacher.present,
    teachingLoad: teacher.teachingLoad,
    teachingLoadHours: teacher.teachingLoadHours,
    description: teacher.description,
    writesCertificate: teacher.writesCertificate,
    person: {
      uuid: teacher.uuid,
      fullName: teacher.fullName,
      displayNameShort: teacher.displayNameShort || teacher.fullName,
      color: teacher.color,
    },
  })),
  durationCountArray: [{ duration: 1, count: 1 }],
});

export const useTeachingBlockLessons = ({ versionUuid }: { versionUuid: string }) => {
  const [loadingState, dispatch] = useReducer(loadingReducer, loadingReducerInitialState);

  const [teachingBlockSubjectContainerUuid, setTeachingBlockSubjectContainerUuid] = useState<string | null>(null);

  const schoolYear = useSelectedSchoolYear();

  const lessonContext = useMemorizedCacheTag('LESSONS');

  const [, create] = useCreateLessonInTeachingBlockVersionMutation();
  const [, update] = useUpdateLessonMutation();
  const [, setFlag] = useSetLessonsFlagMutation();
  const [, assignTeachers] = useAssignTeachersToLessonsMutation();
  const [, detachTeachers] = useDetachTeachersFromLessonsMutation();
  const [, attachToVersion] = useAttachLessonsToTeachingBlockVersionMutation();
  const [, detachFromVersion] = useDetachLessonsFromTeachingBlockVersionMutation();
  const [, copyIntoVersion] = useCopyLessonIntoTeachingBlockVersionMutation();
  const [, deleteCards] = useDeleteTeachingBlockCardsMutation();
  const [, createCards] = useCreateTeachingBlockCardsMutation();
  const [, deleteLessons] = useDeleteLessonsMutation();

  // Kopfdaten und Karten der Version aus dem Lesemodell, das auch das Board lädt.
  const { versionData } = useTeachingBlockVersionData(versionUuid);
  const versionSubjectContainerUuid = versionData?.meta.subjectContainerUuid;

  useEffect(() => {
    if (!teachingBlockSubjectContainerUuid) {
      setTeachingBlockSubjectContainerUuid(versionSubjectContainerUuid ?? null);
    }
  }, [versionSubjectContainerUuid, teachingBlockSubjectContainerUuid]);

  const [{ data: lessonsQueryData }] = useLessonsForTeachingBlockVersionQuery({
    variables: { versionUuid },
    // additionalTypenames, damit der Cache diese Query auch bei leerem Ergebnis als von Lesson
    // abhängig registriert – sonst aktualisiert sie sich nach dem Anlegen der ersten Lesson nicht.
    context: lessonContext,
    pause: !teachingBlockSubjectContainerUuid || !versionUuid || versionUuid === '',
  });

  const lessons = lessonsQueryData?.lessonsForTeachingBlockVersion ?? NO_LESSONS;

  const teachingBlockCards = versionData?.cards;

  const lessonsData: TeachingBlockLessonTableType[] = useMemo(() => {
    const rows = lessons.map((lesson) => {
      const classes = [...lesson.lessonClasses].sort(byGrade).map((lessonClass) => lessonClass.class);

      const res: TeachingBlockLessonTableType = {
        classesUuids: classes.map((c) => c.uuid),
        elective: lesson.elective ?? false,
        onlyInTeachingBlockVersion: lesson.onlyInTeachingBlockVersion,
        // In der Epochenplan-Version sind nur die dort angelegten Zuordnungen bearbeitbar.
        editStatus: lesson.onlyInTeachingBlockVersion ? LessonEditStatus.InVersion : LessonEditStatus.Blocked,
        subjectShortName: lesson.subject.shortName,
        teacherUuids: lesson.teachers.map((teacher) => teacher.uuid),
        teachingLoadEnabled: lesson.teachingLoadEnabled ?? false,
        timetableEnabled: lesson.timetableEnabled ?? false,
        uuid: lesson.uuid,
        groupClassNames: lesson.groupClassNames,
        rooms: lesson.roomSupply.map((room) => ({ uuid: room.uuid, name: room.name })),
        subject: lesson.subject.name,
        subjectUuid: lesson.subject.uuid,
        classSort: lesson.classSort,
        teacherSort: lesson.teachersSort,
        placedCardsCount: lesson.teachingBlockPlacedCardsCount,
        teachers: lesson.teachers.map((teacher) => {
          return {
            uuid: teacher.uuid,
            name: teacher.displayNameShort,
            color: teacher.color,
            teachingLoadEnabled: false,
            presenceEnabled: false,
            teachingLoadHours: 2,
          };
        }),
        classes: classes.map((c) => {
          const res: ClassInterfaceType & {
            groups?: Array<{ uuid: string; shortName?: string; name: string; classFraction?: number }>;
          } = {
            uuid: c.uuid,
            grade: c.grade ?? 0,
            name: c.name,
            shortName: c.shortName,
            gradeGroup: c.gradeGroup,
          };
          return res;
        }),
      };
      return res;
    });
    // sort lessons data first by classSort then by teacherSort
    return rows.sort((a, b) =>
      ((a.classSort ?? '') + a.teacherSort + a.subject).localeCompare((b.classSort ?? '') + b.teacherSort + b.subject),
    );
  }, [lessons]);

  const detachLessons = useCallback(
    async (uuids: string[]) => {
      dispatch({ type: 'SET_LOADING', uuids: uuids });

      if (invalid(uuids)) {
        return { error: 'No or invalide UUID provided for update' };
      }

      const result = await detachTeachers({ uuids }, lessonContext);
      dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
      return result;
    },
    [detachTeachers, lessonContext],
  );

  /** Löscht die Karten der Zuordnungen in dieser Version. */
  const deleteVersionCards = async (uuids: string[]) => {
    const cardUuids = (versionData?.cards ?? [])
      .filter((card) => uuids.includes(card.lessonUuid))
      .map((card) => card.uuid);
    if (cardUuids.length === 0) return { error: undefined };
    return deleteCards({ cardUuids }, { additionalTypenames: ['TeachingBlockCard', 'Lesson'] });
  };

  /** Löscht die Karten der Zuordnungen in dieser Version und danach die Zuordnungen selbst. */
  const deleteCardsAndLessons = async (uuids: string[]) => {
    if (invalid(uuids)) {
      return { error: 'No or invalide UUID provided for update' };
    }

    dispatch({ type: 'SET_LOADING', uuids: uuids });

    if (!versionUuid) {
      dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
      return { error: { message: 'version not found', name: 'version not found' }, success: false };
    }
    const deleteResult = await deleteVersionCards(uuids);
    const lessonsResult = deleteResult.error ? undefined : await deleteLessons({ uuids }, lessonContext);

    const error = deleteResult.error ?? lessonsResult?.error;
    const result = error ? { success: false, error } : { success: true };

    await refreshTeachingBlockVersionQueries(versionUuid);
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });

    return result;
  };

  /** Löscht die Karten der Zuordnungen in dieser Version und löst die Zuordnungen aus ihr. */
  const deactivateLessons = async (uuids: string[]) => {
    if (invalid(uuids)) {
      return { error: 'No or invalide UUID provided for update' };
    }

    dispatch({ type: 'SET_LOADING', uuids: uuids });
    let result: { error?: unknown; success?: boolean };
    if (versionUuid) {
      const resCards = await deleteVersionCards(uuids);
      const resLesson = resCards.error ? undefined : await detachFromVersion({ versionUuid, uuids }, lessonContext);
      const error = resCards.error ?? resLesson?.error;
      result = error ? { error, success: false } : { success: true };
      await refreshTeachingBlockVersionQueries(versionUuid);
    } else {
      result = { error: { message: 'version not found', name: 'version not found' }, success: false };
    }

    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });

    return result;
  };

  /** Nimmt bestehende Zuordnungen in die Version auf und liefert ihren neuen Stand. */
  const attachLessons = async (uuids: string[]) => {
    const result = await attachToVersion({ versionUuid, uuids }, lessonContext);
    return { error: result.error, lessons: result.data?.attachLessonsToTeachingBlockVersion ?? [] };
  };

  const assignLessons = useCallback(
    async (uuids: string[], teachers: { uuid: string }[]) => {
      if (invalid(uuids)) {
        return { error: 'No or invalide UUID provided for update' };
      }

      dispatch({ type: 'SET_LOADING', uuids: uuids });
      const result = await assignTeachers(
        {
          uuids,
          teachers: teachers.map((teacher) => ({
            personUuid: teacher.uuid,
            present: true,
            writesCertificate: true,
            teachingLoad: true,
          })),
        },
        lessonContext,
      );
      dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
      return result;
    },
    [lessonContext, assignTeachers],
  );

  const updateCheckboxes = async ({
    type,
    uuids,
    value,
  }: {
    type: LessonCheckbox;
    uuids: string[];
    value: boolean;
  }) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    const res = await setFlag({ uuids, flag: checkboxFlags[type], value }, lessonContext);
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
    return res;
  };

  /**
   * Legt eine Zuordnung an, die es nur in dieser Version gibt, samt ungesteckter Karten. Ohne
   * `cardDurations` entstehen die Karten aus der Karten-Angabe des Formulars.
   */
  const createLesson = async (values: VersionLessonFormType, cardDurations?: number[]) => {
    return await create(
      {
        versionUuid,
        schoolYearUuid: schoolYear?.uuid ?? '',
        input: toLessonInput(values),
        cardDurations: cardDurations ?? toCardDurations(values),
      },
      lessonContext,
    );
  };

  /**
   * `notOnlyInVersion` („in dieser Version bearbeiten“): das Backend legt von der Deputats-Zuordnung
   * eine Kopie nur für diese Version an, hängt die Karten samt Position, Räumen und Sperre auf die
   * Kopie um und löst das Original aus der Version – die Karten bleiben gesteckt.
   *
   * Sonst („duplizieren“ einer Zuordnung der Version): eine zweite Zuordnung mit eigenen, ungesteckten
   * Karten gleicher Dauer.
   */
  const duplicateLesson = async (uuids: string[], notOnlyInVersion: boolean) => {
    if (invalid(uuids)) {
      return { error: 'No or invalide UUID provided for update' };
    }
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    let result: { error?: unknown; success?: boolean } = {};

    if (notOnlyInVersion) {
      for (const lessonUuid of uuids) {
        const copied = await copyIntoVersion({ versionUuid, lessonUuid }, lessonContext);
        // Der Client wandelt Fehler in eine allgemeine Meldung um; ohne Daten ist die Kopie nicht entstanden.
        if (copied.error || !copied.data) {
          result = { error: copied.error ?? { message: 'copy failed', name: 'copy failed' }, success: false };
          break;
        }
        result = { success: true };
      }
      await refreshTeachingBlockVersionQueries(versionUuid);
    } else {
      const currentLesson = lessons.find((lesson) => uuids.includes(lesson.uuid));
      if (currentLesson) {
        const cards = teachingBlockCards?.filter((card) => card.lessonUuid === currentLesson.uuid);
        result = await createLesson(
          { ...teachingBlockLessonToFormType(currentLesson), uuid: '' },
          cards?.map((card) => card.duration ?? 1) ?? [],
        );
      }
    }
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
    return result;
  };

  const updateLesson = async (values: VersionLessonFormType, uuids: string[]) => {
    if (invalid(uuids)) {
      return { error: { name: '', message: 'No or invalide UUID provided for update' } };
    }
    dispatch({ type: 'SET_LOADING', uuids: [values.uuid ?? 'all'] });

    const result = await update({ uuid: values.uuid ?? '', input: toLessonInput(values) }, lessonContext);
    dispatch({ type: 'REMOVE_LOADING', uuids: [values.uuid ?? 'all'] });
    return result;
  };

  return {
    /** Zuordnungen der Version, wie das Backend sie liefert. */
    lessons,
    lessonsData,
    loadingState,
    teachingBlockSubjectContainerUuid,
    detachLessons,
    deleteCardsAndLessons,
    deactivateLessons,
    attachLessons,
    assignLessons,
    updateCheckboxes,
    duplicateLesson,
    createLesson,
    updateLesson,
    dispatch,
    createCards,
  };
};
